<template>
  <Dialog
    v-model="visible"
    title="New Practice Product"
    dialog-type="primary"
    dialog-size="large"
    :close-on-overlay="false"
    @close="handleClose"
  >
    <template #header-icon>
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
        <line x1="12" y1="22.08" x2="12" y2="12"></line>
      </svg>
    </template>

    <div class="add-product-body">
      <!-- Describe Your Product section -->
      <div class="section-title-row">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M18 0H2C1 0 0 0.9 0 2V5.01C0 5.73 0.43 6.35 1 6.7V18C1 19.1 2.1 20 3 20H17C17.9 20 19 19.1 19 18V6.7C19.57 6.35 20 5.73 20 5.01V2C20 0.9 19 0 18 0ZM17 18H3V7H17V18ZM18 5H2V2H18V5Z" fill="#374957"/>
          <path d="M13 10H7V12H13V10Z" fill="#374957"/>
        </svg>
        <span class="section-title-text">Describe Your Product</span>
      </div>

      <!-- Product Name + Product Type row -->
      <div class="form-row two-col">
        <div class="form-col">
          <label class="field-label">Product Name <span class="required">(Required)</span></label>
          <TextInput
            v-model="form.name"
            size="medium"
            placeholder="Product name ..."
            :clearable="true"
          />
        </div>
        <div class="form-col">
          <label class="field-label">Product Type <span class="required">(Required)</span></label>
          <SingleSelector
            v-model="form.type"
            size="medium"
            :options="typeOptions"
            placeholder="Item or service ..."
          />
        </div>
      </div>

      <!-- Type warning notification -->
      <InlineNotification
        v-if="showTypeWarning"
        theme="warning"
        class="warning-alert type-warning"
        message="Due to book keeping / tax / stock and other potential complications, the product type can't not be changed afterwards"
        :with_close_btn="true"
        @close="showTypeWarning = false"
      />

      <!-- Product Description -->
      <div class="form-field">
        <label class="field-label">Product Description</label>
        <TextArea
          v-model="form.description"
          placeholder="Describe your practice product ..."
          :max_length="200"
          :rows="3"
          :clearable="true"
        />
      </div>

      <!-- Assignees -->
      <div class="form-field">
        <label class="field-label">Assignees <span class="required">(Required)</span></label>
        <SingleSelector
          v-model="form.assign"
          size="medium"
          :options="assignOptions"
          placeholder="Select assignee..."
        />
      </div>

      <!-- Upload Files Section -->
      <div class="upload-section">
        <div class="upload-title">Upload files</div>
        <div class="upload-subtitle">Max file size is xxx kb. Supported file types are xxx, xxx, xxx.</div>
        <div
          class="upload-drop-area"
          :class="{ 'dragging': isDragging, 'has-image': displayImage }"
          @dragover.prevent="isDragging = true"
          @dragleave="isDragging = false"
          @drop.prevent="handleFileDrop"
        >
          <!-- No image state -->
          <div v-if="!displayImage" class="drop-content">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
              <rect width="48" height="48" rx="5" fill="#E0E0E0"/>
              <path d="M38 26H26V38H22V26H10V22H22V10H26V22H38V26Z" fill="#8D8D8D"/>
            </svg>
            <p class="drop-text">Drag and drop your files here to add.</p>
            <p class="drop-or">OR</p>
            <ResponsiveButton
              button_type="filled"
              built_in_theme="dark"
              size="small"
              display_name="Add File"
              @click-button="triggerFileInput"
            />
          </div>

          <!-- Has image state - show large preview in center -->
          <div v-else class="image-preview-container">
            <img v-if="displayImage" :src="displayImage" class="large-preview-image" :alt="form.name" />
            <div class="image-actions">
              <ResponsiveButton
                button_type="filled"
                built_in_theme="dark"
                size="small"
                display_name="Change Image"
                @click-button="triggerFileInput"
              />
              <ResponsiveButton
                button_type="outlined"
                built_in_theme="error"
                size="small"
                display_name="Remove Image"
                @click-button="removeFile"
              />
            </div>
          </div>

          <input
            ref="fileInput"
            type="file"
            accept="image/jpeg,image/png"
            style="display: none;"
            @change="handleFileSelect"
          />
        </div>
      </div>

      <!-- Price & Return Policy Section -->
      <div class="section-divider"></div>
      <div class="section-title-row">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18Z" fill="#374957"/>
          <path d="M10.31 9.2C8.94 8.83 8.49 8.44 8.49 7.85C8.49 7.17 9.12 6.7 10.18 6.7C11.29 6.7 11.72 7.22 11.75 8H13.26C13.22 6.9 12.56 5.9 11.25 5.57V4H9.12V5.55C7.9 5.82 6.93 6.62 6.93 7.87C6.93 9.37 8.18 10.12 10.05 10.55C11.72 10.93 12.02 11.49 12.02 12.07C12.02 12.5 11.71 13.18 10.18 13.18C8.76 13.18 8.22 12.61 8.15 11.88H6.65C6.73 13.28 7.91 14.07 9.12 14.33V16H11.25V14.35C12.48 14.11 13.45 13.39 13.45 12.06C13.45 10.2 11.68 9.57 10.31 9.2Z" fill="#374957"/>
        </svg>
        <span class="section-title-text">Price & Return Policy</span>
      </div>

      <!-- Price input -->
      <div class="form-field">
        <label class="field-label">Price <span class="required">(Required)</span></label>
        <TextInput
          v-model="form.price"
          size="medium"
          placeholder="Price excluded tax"
          :clearable="true"
        />
      </div>

      <!-- Return/Refund toggle -->
      <div class="form-field return-refund-row">
        <div class="return-refund-label">
          <span class="field-label-inline">Can this product be return or refund on the website?</span>
          <span class="field-required">(Required)</span>
        </div>
        <div class="toggle-group">
          <span class="toggle-label">No</span>
          <Switch v-model="form.returnOrRefund" color="dark" />
          <span class="toggle-label">Yes</span>
        </div>
      </div>

      <!-- Return policy info notification -->
      <InlineNotification
        v-if="showReturnInfoAlert"
        theme="info"
        class="info-alert"
        message="Your revenue will be automatically recognized and paid to you after the paid date plus the refund period. Refunds will no longer be available after this period."
        :with_close_btn="true"
        @close="showReturnInfoAlert = false"
      />
    </div>

    <!-- Footer Buttons -->
    <template #footer>
      <ResponsiveButton
        button_type="outlined"
        built_in_theme="error"
        size="medium"
        display_name="Cancel"
        @click-button="handleClose"
      />
      <ResponsiveButton
        button_type="filled"
        built_in_theme="dark"
        size="medium"
        display_name="Save"
        :disabled="!canSave || saving"
        @click-button="handleSave"
      />
    </template>
  </Dialog>
</template>

<script>
import Switch from '@/NewLibrary/Switch.vue';
import {
  createMerchandise,
  getInitializationOfCreateMerchandise,
  uploadFile,
} from '@/api/markup';
import { notify } from '@/utils/notify';

export default {
  name: 'AddProductDialog',
  components: {
    Switch,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    merchPayload: {
      type: Object,
      default: () => ({}),
    },
    isClinic: {
      type: Boolean,
      default: false,
    },
    accountId: {
      type: [String, Number],
      default: '',
    },
    providerOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'created'],
  data() {
    return {
      form: {
        name: '',
        type: '',
        description: '',
        assign: 'all',
        price: '',
        returnOrRefund: true,
      },
      typeOptions: [
        { label: 'Item', value: 'item' },
        { label: 'Service', value: 'service' },
      ],
      showTypeWarning: true,
      showReturnInfoAlert: true,
      saving: false,
      isDragging: false,
      uploadedFile: null,
      returnAndRefundInfo: {},
    };
  },
  computed: {
    visible: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      },
    },
    assignOptions() {
      const options = [
        { label: 'All providers (including future ones)', value: 'all' },
      ];
      if (this.isClinic && this.providerOptions.length > 1) {
        options.push({ label: 'Only me', value: 'me' });
      }
      return options;
    },
    imageFolderName() {
      return 'merch_' + this.accountId;
    },
    displayImage() {
      if (this.uploadedFile) {
        return URL.createObjectURL(this.uploadedFile);
      }
      return null;
    },
    canSave() {
      return this.form.name.trim() &&
             this.form.type &&
             this.form.assign &&
             this.form.price !== '' &&
             parseFloat(this.form.price) >= 0;
    },
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.resetForm();
        this.fetchInitInfo();
      }
    },
    'form.price'(newVal) {
      // Sanitize: digits + at most one decimal point + max 2 decimal places
      if (typeof newVal !== 'string') return;
      let cleaned = newVal.replace(/[^0-9.]/g, '');
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
      }
      if (parts.length === 2 && parts[1].length > 2) {
        cleaned = parts[0] + '.' + parts[1].slice(0, 2);
      }
      if (cleaned !== newVal) {
        this.form.price = cleaned;
      }
    },
  },
  methods: {
    resetForm() {
      this.form = {
        name: '',
        type: '',
        description: '',
        assign: 'all',
        price: '',
        returnOrRefund: true,
      };
      this.showTypeWarning = true;
      this.showReturnInfoAlert = true;
      this.uploadedFile = null;
      this.saving = false;
    },

    async fetchInitInfo() {
      try {
        const params = {
          clinic_id: this.merchPayload.clinic_id,
          customer_id: this.merchPayload.customer_id,
          account_type: this.merchPayload.account_type,
        };
        const { data } = await getInitializationOfCreateMerchandise(params);
        if (data && data.return_and_refund_info) {
          this.returnAndRefundInfo = data.return_and_refund_info;
        }
      } catch (error) {
        console.error('Error fetching init info:', error);
      }
    },

    handleClose() {
      this.visible = false;
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadedFile = file;
      }
      // Reset input so the same file can be re-selected
      event.target.value = '';
    },

    handleFileDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.uploadedFile = file;
      }
    },

    removeFile() {
      this.uploadedFile = null;
    },

    async handleSave() {
      // Validate required fields
      if (!this.form.name.trim()) {
        return;
      }
      if (!this.form.type) {
        return;
      }

      this.saving = true;

      try {
        // Build return_and_refund_info from init defaults + user toggle
        const defaults = this.returnAndRefundInfo || {
          accept_return_refund: true,
          accepted_return_refund_days: 7,
          merchandise_refund_deduction_type: 'no_deduction',
          merchandise_refund_deduction_value: 0,
          merchandise_refund_deduction_reason: '',
        };
        const returnAndRefundInfo = {
          ...defaults,
          accept_return_refund: this.form.returnOrRefund,
        };
        if (!this.form.returnOrRefund) {
          returnAndRefundInfo.accepted_return_refund_days = 0;
        }

        // Handle image upload
        let picture = '';
        if (this.uploadedFile) {
          const timestamp = Date.now();
          const ext = this.uploadedFile.name.split('.').pop();
          const fileName = timestamp + '.' + ext;
          const formData = new FormData();
          formData.append('file', this.uploadedFile, fileName);

          try {
            await uploadFile(this.imageFolderName, formData);
            picture = this.imageFolderName + '/' + fileName;
          } catch (e) {
            console.error('Error uploading image:', e);
          }
        }

        // Build payload (same structure as VA Portal formatDataForPostOrUpdateApi)
        const currencyObj = JSON.parse(localStorage.getItem('currency') || '{"name":"USD","symbol":"$"}');
        const currency = currencyObj.name ? currencyObj.name.toLowerCase() : 'usd';

        const payload = {
          ...this.merchPayload,
          name: this.form.name.trim(),
          type: this.form.type,
          description: this.form.description,
          picture,
          price: parseFloat(parseFloat(this.form.price).toFixed(2)),
          currency,
          status: 'active',
          return_and_refund_info: returnAndRefundInfo,
          stock: null,
        };

        // Provider assignment
        if (this.isClinic) {
          payload.apply_to_self = this.form.assign === 'me';
          payload.apply_to_all_providers = this.form.assign === 'all';
          payload.apply_merchandise_info_to = [];
        }

        const { data } = await createMerchandise(payload);
        notify({ type: 'success', title: 'Product Created', message: 'Your product has been created successfully.' });
        this.$emit('created', data);
        this.handleClose();
      } catch (error) {
        console.error('Error creating merchandise:', error);
        notify({ type: 'error', title: 'Error', message: 'Failed to create product. Please try again.' });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Body wrapper inside Dialog's default slot */
.add-product-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 0;
  transition: opacity 0.2s ease;
}

.section-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title-text {
  color: var(--TrueBlack, #333);
  font-family: Inter;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.15px;
}

.form-row.two-col {
  display: flex;
  gap: 16px;
}

.form-row.two-col .form-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-field {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  color: var(--color-Grey-Grey-4A, #4A4A4A);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
}

.field-label-inline {
  color: var(--TrueBlack, #333);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.required {
  color: var(--ErrorRed, #D10000);
  font-weight: 500;
}

.field-required {
  color: var(--ErrorRed, #D10000);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
}

.warning-alert,
.info-alert {
  margin-top: 4px;
}

.section-divider {
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
  margin: 8px 0;
}

.return-refund-row {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0;
}

.return-refund-label {
  display: flex;
  align-items: center;
  gap: 4px;
}

.toggle-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toggle-label {
  color: var(--TrueBlack, #333);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

/* Upload section */
.upload-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.upload-title {
  color: var(--TrueBlack, #333);
  font-family: Inter;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
}

.upload-subtitle {
  color: #979797;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
}

.upload-drop-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 12px;
  border-radius: 8px;
  border: 2px dashed var(--DividerGray, #C4C4C4);
  background: #F5F5F5;
  min-height: 120px;
  transition: border-color 0.3s, background 0.3s;
}

.upload-drop-area.has-image {
  min-height: 200px;
  padding: 20px;
}

.upload-drop-area.dragging {
  border-color: #004879;
  background: rgba(0, 72, 121, 0.04);
}

.drop-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.drop-text,
.drop-or {
  color: #979797;
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  margin: 0;
}

.image-preview-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.large-preview-image {
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 8px;
}

.image-actions {
  display: flex;
  gap: 12px;
}

/* Make TextArea / SingleSelector / TextInput fill the form column */
.form-col :deep(.single-selector-unit-wrapper),
.form-field :deep(.single-selector-unit-wrapper) {
  width: 100%;
}
</style>
